// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { Fragment, useState, useEffect } from 'react';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash';
import { push } from 'connected-react-router';
import {
  withStyles,
 } from '@material-ui/core';
import { bool, object, arrayOf, number, func, shape } from 'prop-types';

import Header from './Header';
import Actions from './Actions';
import Metadata from './Metadata';
import Apis from './Apis';
import Deployments from './Deployments';
import {
  TabsContainer,
  TabPanel,
} from '../../../components';
import DetailsContainer from '../../details';
import { fetchGatewayBundleDetails, fetchGatewayBundleUsage, deleteGatewayBundle } from '../../../actions/gatewayBundle';
import { getConfig, getUserDetails } from '../../../reducers/portalConfig';
import {
  getGatewayBundleDetails,
  getGatewayBundleApiUsage,
  getIsLoading,
  getErrors,
  getDeleteBundleErrors,
  getIsBundleDeleteSuccess,
} from '../../../reducers/gatewayBundle';
import styles from './styles';
import { getI18nFormattedMessage } from '../../../utils/intl';
import { getTabIndex } from '../../../utils';

const getTabItems = () => [
  {
    id: 'overview',
    tabId: 'gateway-bundle-metadata-tab',
    label: getI18nFormattedMessage('label.gateway.bundle.details.page.metadata.title'),
    visible: true,
  },
  {
    id: 'deployments',
    tabId: 'gateway-bundle-deployment-tab',
    label: getI18nFormattedMessage('label.gateway.bundle.details.page.deployments.title'),
    visible: true,
  },
  {
    id: 'apis',
    tabId: 'gateway-bundle-api-tab',
    label: getI18nFormattedMessage('label.gateway.bundle.details.page.api.title'),
    visible: true,
  },
];
const getTabs = (tabItems) => tabItems.filter(({ visible }) => visible);

export const GatewayBundleDetails = (props) => {
  const {
    gatewayBundleDetails,
    isLoading,
    config,
    userContext,
    bundleErrors,
    apiUsage,
    history,
  } = props;

  const bundleUuid = get(props, 'match.params.bundleUuid');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const tabItems = getTabItems();
  const tabs = getTabs(tabItems);
  const defaultIndex = getTabIndex(tabs, props.match);
  const [tabIndex, setTabIndex] = useState(defaultIndex);
  const [currentTab, setCurrentTab] = useState(tabs[defaultIndex].tabId);

  useEffect(() => {
    if (bundleUuid) {
      props.fetchGatewayBundleDetails(bundleUuid);
      props.fetchGatewayBundleUsage(bundleUuid);
    }
  }, []);

  useEffect(() => {
    const defaultIndex = getTabIndex(tabs, props.match);
     setTabIndex(defaultIndex);
     setCurrentTab(tabs[defaultIndex].tabId);
 }, [props.match]);

  useEffect(() => {
    if (!userContext.portalAdmin) { props.push('/404'); }
  }, [userContext]);

  useEffect(() => {
    if (!isEmpty(bundleErrors)) {
      props.push('/404');
    }
  }, [bundleErrors]);

  const notifyMessages = (status, message) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };

  const handleTabChange = (e, newValue) => {
    setTabIndex(newValue);
    setCurrentTab(tabs[newValue].tabId);
    history.push(`/publish/gateway-bundles/details/${bundleUuid}/${tabs[newValue].id}`)
  };

  return (
    <DetailsContainer
      detailsPageId="gateway-bundle-details-page"
      isLoading={isLoading}
      notificationId="gateway-bundle-details-notification"
      notificationStatus={notificationStatus}
      setNotificationStatus={setNotificationMessage}
      notificationMessage={notificationMessage}
      setNotificationMessage={setNotificationMessage}
      header={
        <Header
          gatewayBundleDetails={gatewayBundleDetails}
        />
      }
      actions={
        <Actions
          {...props}
          notifyMessages={notifyMessages}
        />
      }
      tabs={
        <TabsContainer
          id={'gateway-bundle-details-tab-container'}
          tabValue={tabIndex}
          orientation="horizontal"
          tabItems={tabs}
          handleTabChange={handleTabChange}
        />
      }
      page={
        <Fragment>
          <TabPanel
            visible={currentTab === 'gateway-bundle-metadata-tab'}
            id="gateway-bundle-metadata-tab-panel"
          >
            <Metadata
              gatewayBundleDetails={gatewayBundleDetails}
              apiUsage={apiUsage}
              config={config}
            />
          </TabPanel>
          <TabPanel
            visible={currentTab === 'gateway-bundle-deployment-tab'}
            id="gateway-bundle-deployments-tab-panel"
          >
            <Deployments
              gatewayBundleDetails={gatewayBundleDetails}
              bundleUuid={bundleUuid}
              notifyMessages={notifyMessages}
            />
          </TabPanel>
          <TabPanel
            visible={currentTab === 'gateway-bundle-api-tab'}
            id="gateway-bundle-api-tab-panel"
          >
            <Apis
              gatewayBundleDetails={gatewayBundleDetails}
              bundleUuid={bundleUuid}
            />
          </TabPanel>
        </Fragment>
      }
    />
  );
};

const mapStateToProps = state => ({
  config: getConfig(state),
  gatewayBundleDetails: getGatewayBundleDetails(state),
  apiUsage: getGatewayBundleApiUsage(state),
  isLoading: getIsLoading(state),
  userContext: getUserDetails(state),
  bundleErrors: getErrors(state),
  deleteBundleErrors: getDeleteBundleErrors(state),
  isBundleDeleteSuccess: getIsBundleDeleteSuccess(state),
});

const mapDispatchToProps = {
  fetchGatewayBundleDetails,
  fetchGatewayBundleUsage,
  deleteGatewayBundle,
  push,
};

GatewayBundleDetails.propTypes = {
  gatewayBundleDetails: object,
  apiUsage: number,
  isLoading: bool,
  config: object,
  userContext: object,
  bundleErrors: arrayOf(object),
  fetchGatewayBundleDetails: func,
  fetchGatewayBundleUsage: func,
  push: func,
  history: shape({}),
  match: shape({}),
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(GatewayBundleDetails);
